/**
 * editor2/ReservationEditor.tsx
 * ReservationEditor v2 メインコンテナコンポーネント
 * hooks とセクションを組み合わせるワイヤリング層（ビジネスロジックは hooks/sections に委譲）
 */
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Snackbar, Alert } from '@mui/material';
import '../../styles/gridCalendar.scss';

// Types
import type { ReservationEditorProps, EditorReservation, MemoBlock } from './types';
import { SAFE_DEFAULTS, emailTemplateLabel } from './constants';

// Hooks
import useReservationEditor from './hooks/useReservationEditor';
import useGenericEdit from './hooks/useGenericEdit';
import usePaymentEmail from './hooks/usePaymentEmail';
import useSquareInvoice from './hooks/useSquareInvoice';
import useMemoTimeline from './hooks/useMemoTimeline';
import { useReservationStatusChange } from '../../hooks/useReservationStatusChange';

// Sections
import {
  BasicInfoSection,
  StatusSection,
  ScheduleSection,
  HotelDeliverySection,
  CruiseSection,
  VehicleSection,
  OptionsSection,
  CustomerSection,
  PricingSection,
  CheckFlagsSection,
  CommunicationSection,
  MemoSection,
  StaffSection,
  EmergencySection,
  ColorSection,
  EmailTemplateSection,
  DetailSettingsSection,
} from './sections';

// Modals
import GenericEditDialog from './modals/GenericEditDialog';
import MemoBlockEditModal from './modals/MemoBlockEditModal';
import RecalcModal from './modals/RecalcModal';
import PaymentEmailModal from './modals/PaymentEmailModal';
import { buildMemoEntry } from '../calendar2/memoLog';
import { appendReservationMemo } from '../calendar2/saveScheduleChange';
import { logEvent } from '../../lib/eventLog';
import JsonDataModal from './modals/JsonDataModal';
import ConflictDialog from './modals/ConflictDialog';
import { EditorFieldStateContext, MARK_COLORS } from './conflict/fieldState';
import { fieldLabel, formatFieldValue } from './conflict/threeWayMerge';

// sysApiBase は定数から取得
import { SYS_API_BASE } from '../../constants/calendar';

export default function ReservationEditor({
  open,
  editing,
  disabled,
  onChange,
  onCancel,
  onSave,
  registerNameInputRef,
  reservationStatusList,
  vehicles,
  vehicleTypes,
  onAssignClick,
}: ReservationEditorProps) {
  // --- スライドアニメーション管理 ---
  // editing のID変化を検知して、閉じ→開きを自動制御
  const [panelVisible, setPanelVisible] = useState(open);
  const panelRef = useRef<HTMLDivElement>(null);
  const lastEditingRef = useRef<any>(editing);
  const prevEditingIdRef = useRef<string | null>(null);
  const pendingEditingRef = useRef<any>(null);

  // editing が変わるたびに最新を保持（閉じアニメーション中の表示用）
  useEffect(() => {
    if (editing) {
      lastEditingRef.current = editing;
    }
  }, [editing]);

  useEffect(() => {
    const currentId = editing ? String(editing.id ?? editing.page_id ?? '') : null;
    const prevId = prevEditingIdRef.current;

    if (open && editing) {
      if (prevId && prevId !== currentId) {
        // 別の予約に切り替え → まずスライドアウト、完了後にスライドイン
        pendingEditingRef.current = editing;
        setPanelVisible(false);
      } else if (!panelVisible) {
        // 新規オープン
        prevEditingIdRef.current = currentId;
        setPanelVisible(true);
      } else {
        // 同じ予約の更新（フィールド編集等）
        prevEditingIdRef.current = currentId;
      }
    } else if (!open && panelVisible) {
      prevEditingIdRef.current = null;
      pendingEditingRef.current = null;
      setPanelVisible(false);
    }
  }, [open, editing]);

  // transitionend でスライドアウト完了を検知
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;

    const handleTransitionEnd = (e: TransitionEvent) => {
      if (e.propertyName !== 'transform') return;
      if (!panelVisible && pendingEditingRef.current) {
        // 閉じアニメーション完了 → 待機中の予約でスライドイン
        const pending = pendingEditingRef.current;
        prevEditingIdRef.current = String(pending.id ?? pending.page_id ?? '');
        pendingEditingRef.current = null;
        setPanelVisible(true);
      }
    };

    el.addEventListener('transitionend', handleTransitionEnd);
    return () => el.removeEventListener('transitionend', handleTransitionEnd);
  }, [panelVisible]);

  // --- Derived state ---
  // panelVisible中は editing を、スライドアウト中は lastEditingRef を表示
  const displayEditing = editing ?? lastEditingRef.current;
  const safe: EditorReservation = (displayEditing ?? SAFE_DEFAULTS) as EditorReservation;
  const isNew = Boolean(displayEditing?.__isNew || displayEditing?.__isCopy);
  const sysApiBase = SYS_API_BASE;

  // --- Toast state ---
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'info' | 'success' | 'error'; duration: number | null }>({
    open: false, message: '', severity: 'info', duration: 5000,
  });

  // --- Modal open states ---
  const [memoBlockEditBlock, setMemoBlockEditBlock] = useState<MemoBlock | null>(null);
  const [recalcOpen, setRecalcOpen] = useState(false);
  const [jsonModalOpen, setJsonModalOpen] = useState(false);

  // --- Data getter (stable ref for hooks) ---
  const getData = useCallback(() => (editing ?? SAFE_DEFAULTS) as EditorReservation, [editing]);

  // --- Hooks ---
  const notify = useCallback((message: string, severity: 'info' | 'success' | 'error') => {
    setToast({ open: true, message, severity, duration: severity === 'error' ? 10000 : 5000 });
  }, []);
  const editor = useReservationEditor({
    open,
    editing,
    onChange,
    onSave,
    statusOptions: reservationStatusList,
    onNotice: notify,
  });

  // 請求書発行など、パネル内の別経路からの保存も三方向マージを通す
  // （素の onSave に editing 全量を渡すと他端末の変更を先祖返りさせる）
  const saveViaMerge = useCallback(
    async (data: EditorReservation, options?: { keepOpen?: boolean }) => {
      const ok = await editor.saveMerged(data, options);
      if (!ok) throw new Error('保存を中止しました');
    },
    [editor.saveMerged],
  );

  const genericEdit = useGenericEdit({
    getData,
    onChange,
    statusOptions: reservationStatusList,
  });

  const paymentEmail = usePaymentEmail({
    getData,
    sysApiBase,
  });

  const squareInvoice = useSquareInvoice({
    getData,
    onChange,
    onSave: saveViaMerge,
    sysApiBase,
  });

  const memoTimeline = useMemoTimeline();

  // --- 予約ステータス即保存フック ---
  const statusChange = useReservationStatusChange({
    reservationStatusList: reservationStatusList || [],
    getReservationData: () => editing,
    onReservationStatusSaved: (reservationId, newStatus) => {
      // UIに反映（フォーム全体は保存しない、ステータスだけ更新）
      // markSaved() だと他の未保存変更まで「保存済み」扱いになり消えるため、status だけ base を進める
      onChange?.({ status: String(newStatus) });
      editor.markFieldsSaved({ status: String(newStatus) });
    },
    onToast: (message) => {
      console.log('[StatusChange]', message);
    },
  });

  const handleStatusChange = useCallback(
    (newStatusId: string | number) => {
      if (!editing) return;
      const reservationId = String(editing.id || editing.page_id);
      const vehicleId = editing.vehicle_id ? String(editing.vehicle_id) : null;
      statusChange.requestStatusChange(reservationId, newStatusId, editing.status, vehicleId);
    },
    [editing, statusChange],
  );

  // --- Callbacks ---

  const handleRecalcApply = useCallback(
    (sum: number) => {
      onChange?.({ sum_price: sum });
      setRecalcOpen(false);
    },
    [onChange],
  );

  // 支払い確認トグル。以前は ON 時にメール作成モーダルを自動起動していたが、
  // 誤操作・保存忘れの温床になっていたため廃止（2026-07-26）。単純なトグルのみ。
  // メール作成は従来どおり支払いセクションのテンプレートボタン（onOpenEmailTemplate）から。
  const handlePaymentCheckChange = useCallback(
    (checked: boolean) => {
      onChange?.({ payment: checked });
    },
    [onChange],
  );

  const handleMemoBlockSave = useCallback(
    (newContent: string) => {
      if (memoBlockEditBlock == null) return;
      const updatedMemo = memoTimeline.updateMemoBlock(
        safe.memo || '',
        memoBlockEditBlock.originalIdx,
        newContent,
      );
      onChange?.({ memo: updatedMemo });
      setMemoBlockEditBlock(null);
    },
    [safe.memo, memoBlockEditBlock, onChange, memoTimeline],
  );

  const handleJsonSave = useCallback(
    (newJsonData: any) => {
      onChange?.({ json_data: newJsonData });
      setJsonModalOpen(false);
    },
    [onChange],
  );

  const handlePaymentEmailSend = useCallback(
    (emailData: { to: string; cc?: string; bcc?: string; subject: string; body: string; templateType?: string }) => {
      // 1. モーダルを即閉じ、トーストで「送信中」を表示
      paymentEmail.closePaymentEmail();
      setToast({ open: true, message: `${emailData.to} へメールを送信しています...`, severity: 'info', duration: null });

      // 2. 非同期で送信
      const url = `${sysApiBase}/bulk_send.php`;
      console.log('[Email] 送信URL:', url);
      console.log('[Email] payload:', emailData);
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailData),
      })
        .then((res) => {
          console.log('[Email] レスポンスステータス:', res.status, res.statusText);
          console.log('[Email] レスポンスヘッダー:', [...res.headers.entries()]);
          return res.text();
        })
        .then((text) => {
          console.log('[Email] レスポンスボディ:', text.slice(0, 500));
          let result: any;
          try {
            result = JSON.parse(text);
          } catch {
            console.error('[Email] JSON parse失敗。レスポンス全文:', text);
            setToast({ open: true, message: 'メール送信エラー: サーバーから不正なレスポンスが返されました', severity: 'error', duration: 10000 });
            return;
          }
          if (result.success || result.status === 'success') {
            logEvent({
              action: 'email.send', target: safe?.id, result: 'ok',
              detail: { template: emailData.templateType, cc: Boolean(emailData.cc), bcc: Boolean(emailData.bcc) },
            });
            setToast({ open: true, message: 'メールを正常に送信しました', severity: 'success', duration: 5000 });
            // 予約メモに送信記録を残す（失敗しても送信自体には影響させない）
            const rid = safe?.id;
            if (rid && !String(rid).startsWith('tmp_')) {
              const ccLine = emailData.cc ? `\nCC: ${emailData.cc}` : '';
              const bccLine = emailData.bcc ? `\nBCC: ${emailData.bcc}` : '';
              const entry = buildMemoEntry(
                'メール送信',
                `メール送信済み（${emailTemplateLabel(emailData.templateType)}）\n宛先: ${emailData.to}${ccLine}${bccLine}\n件名: ${emailData.subject}`,
              );
              appendReservationMemo(rid, entry).then((saved) => {
                // ★ここが 2026-07 に無言で失敗していた箇所。成否を必ずサーバに残す
                logEvent({
                  action: 'memo.append', target: rid, result: saved ? 'ok' : 'fail',
                  detail: { source: 'email.send' },
                });
                if (!saved) {
                  // 送信自体は成功しているので再送はしない。ただし黙って捨てない。
                  setToast({ open: true, message: 'メールは送信しましたが、メモへの記録に失敗しました', severity: 'error', duration: 8000 });
                }
              });
            }
          } else {
            logEvent({ action: 'email.send', target: safe?.id, result: 'fail', err: result.message, detail: { template: emailData.templateType } });
            setToast({ open: true, message: 'メール送信に失敗しました: ' + (result.message || ''), severity: 'error', duration: 10000 });
          }
        })
        .catch((err) => {
          logEvent({ action: 'email.send', target: safe?.id, result: 'fail', err, detail: { template: emailData.templateType } });
          console.error('[Email] fetch失敗:', err.message, err);
          setToast({ open: true, message: `メール送信エラー: ${err.message}`, severity: 'error', duration: 10000 });
        });
    },
    [sysApiBase, safe?.id],
  );

  // --- Shared section props ---
  const sectionProps = {
    data: safe,
    onChange,
    disabled,
    isNew,
    onOpenEdit: genericEdit.handleOpenEdit,
    onOpenEditMulti: genericEdit.handleOpenEditMulti,
  };

  // --- Save button label ---
  const saveLabel = editor.isCopyReservation
    ? 'コピーする'
    : editor.isNewReservation
      ? '新規保存'
      : '保存';

  const panelTitle = isNew ? '新規予約' : `予約 - ${safe.page_id || safe.id}`;

  // --- 変更サマリー（ヘッダー直下）: チップをクリックすると該当フィールドへスクロール ---
  const scrollToField = useCallback((field: string) => {
    const root = panelRef.current;
    if (!root) return;
    const el = root.querySelector<HTMLElement>(`[data-field~="${field}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('gc-field--flash');
    window.setTimeout(() => el.classList.remove('gc-field--flash'), 1200);
  }, []);

  const fieldStateValue = useMemo(() => ({ marks: editor.fieldMarks }), [editor.fieldMarks]);
  const remoteConflicts = editor.remoteChanges.filter((rc) => editor.fieldMarks[rc.field] === 'conflict');
  const remoteFolded = editor.remoteChanges.filter((rc) => editor.fieldMarks[rc.field] === 'remote');
  const latestRemoteAt = editor.remoteChanges.reduce<string | null>((acc, rc) => {
    if (!rc.at) return acc;
    return !acc || new Date(rc.at).getTime() > new Date(acc).getTime() ? rc.at : acc;
  }, null);
  const fmtClock = (iso: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };
  const chipStyle = (kind: 'dirty' | 'remote' | 'conflict'): React.CSSProperties => ({
    display: 'inline-block',
    padding: '1px 8px',
    borderRadius: 10,
    fontSize: '0.72rem',
    fontWeight: 600,
    color: MARK_COLORS[kind].text,
    background: '#fff',
    border: `1px solid ${MARK_COLORS[kind].border}`,
    cursor: 'pointer',
    lineHeight: '18px',
  });

  return (
    <EditorFieldStateContext.Provider value={fieldStateValue}>
      {/* Backdrop */}
      <div
        className={`gc-sidepanel-backdrop ${panelVisible ? 'visible' : 'hidden'}`}
        onClick={onCancel}
        aria-hidden="true"
      />

      {/* Side panel */}
      <aside
        ref={panelRef}
        className={`gc-sidepanel ${panelVisible ? 'open' : 'closed'}`}
        role="dialog"
        aria-modal={panelVisible ? 'true' : 'false'}
        aria-hidden={panelVisible ? 'false' : 'true'}
      >
        {/* Sticky header */}
        <div className="gc-sidepanel-header">
          <div className="gc-sidepanel-title">
            <strong>{panelTitle}</strong>
          </div>
          <div className="gc-actions">
            <button
              type="button"
              className="gc-btn"
              onClick={onCancel}
              style={{ backgroundColor: '#ef4444', color: '#fff', border: 'none' }}
            >
              閉じる
            </button>
            <button
              type="button"
              className={`gc-btn ${editor.isDirty ? 'gc-btn--primary' : 'gc-btn--secondary'}`}
              onClick={editor.handleSave}
              disabled={disabled || !editor.isDirty || editor.isSaving}
              style={{
                backgroundColor: editor.isDirty && !editor.isSaving ? '#3b82f6' : '#9ca3af',
                color: '#fff',
                cursor: editor.isDirty && !editor.isSaving ? 'pointer' : 'default',
              }}
            >
              {editor.isSaving ? <CircularProgress size={20} color="inherit" /> : saveLabel}
            </button>
          </div>
        </div>

        {/* 変更サマリー: 自分が変更中のフィールド */}
        {!isNew && editor.myChangedFields.length > 0 && (
          <div
            className="gc-change-summary gc-change-summary--dirty"
            style={{
              margin: '8px 4px 0',
              padding: '6px 10px',
              borderRadius: 6,
              background: MARK_COLORS.dirty.bg,
              borderLeft: `3px solid ${MARK_COLORS.dirty.border}`,
              fontSize: '0.78rem',
              color: MARK_COLORS.dirty.text,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              alignItems: 'center',
            }}
          >
            <strong>✎ 変更中 {editor.myChangedFields.length}件（未保存）:</strong>
            {editor.myChangedFields.map((f) => (
              <button
                key={f}
                type="button"
                style={{
                  ...chipStyle(editor.fieldMarks[f] === 'conflict' ? 'conflict' : 'dirty'),
                  fontFamily: 'inherit',
                }}
                onClick={() => scrollToField(f)}
                title={`${fieldLabel(f)} へ移動`}
              >
                {fieldLabel(f)}
              </button>
            ))}
          </div>
        )}

        {/* 他端末の更新バナー */}
        {!isNew && editor.remoteChanges.length > 0 && (
          <div
            className="gc-change-summary gc-change-summary--remote"
            style={{
              margin: '6px 4px 0',
              padding: '6px 10px',
              borderRadius: 6,
              background: remoteConflicts.length > 0 ? MARK_COLORS.conflict.bg : MARK_COLORS.remote.bg,
              borderLeft: `3px solid ${remoteConflicts.length > 0 ? MARK_COLORS.conflict.border : MARK_COLORS.remote.border}`,
              fontSize: '0.78rem',
              color: remoteConflicts.length > 0 ? MARK_COLORS.conflict.text : MARK_COLORS.remote.text,
            }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              <strong>⟳ 他の端末で更新されました{latestRemoteAt ? `（${fmtClock(latestRemoteAt)}）` : ''}</strong>
              {remoteFolded.map((rc) => (
                <button
                  key={rc.field}
                  type="button"
                  style={{ ...chipStyle('remote'), fontFamily: 'inherit' }}
                  onClick={() => scrollToField(rc.field)}
                  title={`${fieldLabel(rc.field)}: ${formatFieldValue(rc.field, rc.value, reservationStatusList)}`}
                >
                  {fieldLabel(rc.field)}
                </button>
              ))}
              {remoteConflicts.map((rc) => (
                <button
                  key={rc.field}
                  type="button"
                  style={{ ...chipStyle('conflict'), fontFamily: 'inherit' }}
                  onClick={() => scrollToField(rc.field)}
                  title={`最新値: ${formatFieldValue(rc.field, rc.value, reservationStatusList)}`}
                >
                  ⚠ {fieldLabel(rc.field)}
                </button>
              ))}
            </div>
            <div style={{ marginTop: 3, fontSize: '0.72rem', opacity: 0.85 }}>
              {remoteFolded.length > 0 && 'あなたが触っていない項目は最新の値に更新済みです。'}
              {remoteConflicts.length > 0 && ' ⚠ の項目はあなたも変更しているため、保存時にどちらを残すか確認します。'}
            </div>
          </div>
        )}

        {/* Scrollable form body */}
        <div
          className="gc-form"
          style={{ padding: '0 16px', paddingBottom: '50px', display: 'flex', flexDirection: 'column' }}
        >
          {/* 1. 予約名・予約コード・確認同意・コメント・ショートメモ */}
          <BasicInfoSection
            {...sectionProps}
            onInvoiceOpen={squareInvoice.openInvoiceLink}
            onInvoiceCreate={() => squareInvoice.createInvoice(editor.isDirty)}
            onInvoiceVoid={squareInvoice.voidInvoice}
            isCreatingInvoice={squareInvoice.isCreatingInvoice}
            isVoidingInvoice={squareInvoice.isVoidingInvoice}
          />

          {/* 2. 確認・同意状況 */}
          <CheckFlagsSection {...sectionProps} onPaymentCheckChange={handlePaymentCheckChange} />

          {/* 3. メモ（タイムライン + アクションボタン） */}
          <MemoSection
            {...sectionProps}
            onOpenMemoBlockEdit={(block: MemoBlock) => setMemoBlockEditBlock(block)}
          />

          {/* 4. 利用期間・希望時間 */}
          <ScheduleSection {...sectionProps} />

          {/* 4b. ホテル送迎（LP のホテル配車＝貸出場所指定。json_data を表示・編集） */}
          <HotelDeliverySection {...sectionProps} onJsonModalOpen={() => setJsonModalOpen(true)} />

          {/* 4c. クルーズ船（クルーズ船の予約だけ。船・プラン・返却の期限・事前決済・お渡し場所の案内） */}
          <CruiseSection {...sectionProps} />

          {/* 5. 連絡手段 */}
          <CommunicationSection {...sectionProps} />

          {/* 6. 予約コード・車両コード・車両タイプ・メール・電話 */}
          <VehicleSection
            {...sectionProps}
            vehicles={vehicles}
            vehicleTypes={vehicleTypes}
            onAssignClick={onAssignClick}
          />

          <CustomerSection {...sectionProps} />

          {/* 7. ステータス・ブッキング */}
          <StatusSection
            {...sectionProps}
            reservationStatusList={reservationStatusList}
            onStatusChange={handleStatusChange}
            isSavingStatus={statusChange.isSaving}
          />

          {/* 8. 料金 */}
          <PricingSection {...sectionProps} onRecalcClick={() => setRecalcOpen(true)} />

          {/* 9. オプション・その他 */}
          <OptionsSection {...sectionProps} />

          {/* 10. スタッフ */}
          <StaffSection {...sectionProps} />

          {/* 11. 緊急連絡先 */}
          <EmergencySection {...sectionProps} />

          {/* 12. 詳細設定（請求書・色・メールフラグ・メールテンプレート） */}
          {/* 12. メールテンプレート */}
          <EmailTemplateSection
            {...sectionProps}
            onOpenEmailTemplate={(type: string) => paymentEmail.openPaymentEmail(type)}
          />

          {/* 13. 詳細設定（メールフラグ等。請求書操作は基本情報のSquare請求書へ移設） */}
          <DetailSettingsSection
            {...sectionProps}
            onJsonModalOpen={() => setJsonModalOpen(true)}
          />

          {/* 13. 背景色・テキスト色 */}
          <ColorSection {...sectionProps} />
        </div>
      </aside>

      {/* --- Modals --- */}
      <GenericEditDialog
        open={genericEdit.genericEditConfig !== null}
        config={genericEdit.genericEditConfig}
        onSave={genericEdit.handleSaveGenericEdit}
        onCancel={genericEdit.handleCancelGenericEdit}
        onValueChange={genericEdit.setGenericEditValue}
      />

      <MemoBlockEditModal
        open={memoBlockEditBlock !== null}
        block={memoBlockEditBlock}
        onSave={handleMemoBlockSave}
        onCancel={() => setMemoBlockEditBlock(null)}
      />

      <RecalcModal
        open={recalcOpen}
        data={safe}
        onApply={handleRecalcApply}
        onCancel={() => setRecalcOpen(false)}
      />

      <PaymentEmailModal
        open={paymentEmail.modalState.open}
        editing={safe}
        sysApiBase={sysApiBase}
        onClose={paymentEmail.closePaymentEmail}
        onSend={handlePaymentEmailSend}
        state={paymentEmail.modalState}
        onStateChange={paymentEmail.setModalState}
        onLangSelect={paymentEmail.handleLangSelect}
        onAiImprove={paymentEmail.handleAiImprove}
        onAiCompose={paymentEmail.handleAiCompose}
        onRestoreVersion={paymentEmail.restoreVersion}
      />

      <JsonDataModal
        open={jsonModalOpen}
        jsonData={safe.json_data}
        onSave={handleJsonSave}
        onCancel={() => setJsonModalOpen(false)}
        disabled={disabled}
      />

      {/* 同時編集の競合確認（保存時） */}
      <ConflictDialog
        open={editor.conflictDialog.open}
        conflicts={editor.conflictDialog.conflicts}
        theirsUpdatedAt={editor.conflictDialog.theirsUpdatedAt}
        theirMemoAdded={editor.conflictDialog.theirMemoAdded}
        statusOptions={reservationStatusList}
        onResolve={(r) => editor.resolveConflictDialog(r)}
        onCancel={() => editor.resolveConflictDialog(null)}
      />

      {/* ステータス変更確認ダイアログ（チェック初期化など） */}
      <Dialog
        open={statusChange.confirmDialog.open}
        onClose={() => statusChange.closeConfirmDialog(false)}
      >
        <DialogTitle>確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {statusChange.confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => statusChange.closeConfirmDialog(false)}>
            {statusChange.confirmDialog.noLabel || 'いいえ'}
          </Button>
          <Button onClick={() => statusChange.closeConfirmDialog(true)}>
            {statusChange.confirmDialog.yesLabel || 'はい'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={toast.duration}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </EditorFieldStateContext.Provider>
  );
}
